// Pure canvas-painting helper, shared by the live preview and the exporter so
// the recorded output matches what the user sees.

export function renderFrame(
  ctx,
  { width, height, time, duration, background, captions }
) {
  // 1. Background fill
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, width, height);

  if (background?.type === "color") {
    ctx.fillStyle = background.color || "#000";
    ctx.fillRect(0, 0, width, height);
  } else if (background?.type === "image" && background.img) {
    drawCover(ctx, background.img, width, height);
  } else if (background?.type === "video" && background.video) {
    drawCover(ctx, background.video, width, height);
  }

  // 2. Captions
  const captionText = pickCaption(captions, time);
  if (captionText) {
    drawCaption(ctx, captionText, width, height);
  }
}

function drawCover(ctx, source, w, h) {
  const sw = source.videoWidth || source.naturalWidth || source.width;
  const sh = source.videoHeight || source.naturalHeight || source.height;
  if (!sw || !sh) return;
  const ratio = Math.max(w / sw, h / sh);
  const dw = sw * ratio;
  const dh = sh * ratio;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;
  ctx.drawImage(source, dx, dy, dw, dh);
}

function pickCaption(captions, time) {
  if (!captions) return "";
  if (typeof captions === "string") return captions;
  if (captions.text && !captions.items) return captions.text;
  if (Array.isArray(captions.items)) {
    const hit = captions.items.find(
      (c) => time >= c.start && time <= c.end
    );
    return hit?.text || "";
  }
  return "";
}

function drawCaption(ctx, text, width, height) {
  ctx.save();
  const fontSize = Math.round(height * 0.05);
  ctx.font = `700 ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // Wrap text into lines that fit ~85% of width.
  const maxWidth = width * 0.85;
  const lines = wrapText(ctx, text, maxWidth);

  const lineHeight = fontSize * 1.25;
  const blockHeight = lines.length * lineHeight;
  const startY = height - height * 0.08 - blockHeight + lineHeight;

  // Translucent backdrop pill
  const padX = fontSize * 0.7;
  const padY = fontSize * 0.4;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.beginPath();
  const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const boxW = widest + padX * 2;
  const boxH = blockHeight + padY * 2;
  const boxX = (width - boxW) / 2;
  const boxY = startY - lineHeight + padY * 0.3 - padY;
  roundRect(ctx, boxX, boxY, boxW, boxH, fontSize * 0.5);
  ctx.fill();

  // Text
  ctx.fillStyle = "#fff";
  ctx.shadowColor = "rgba(0,0,0,0.85)";
  ctx.shadowBlur = 6;
  lines.forEach((line, i) => {
    ctx.fillText(line, width / 2, startY + i * lineHeight);
  });
  ctx.restore();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? current + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 4); // hard cap
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
}
